import { Link, useLocation } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { Sidebar } from "./Sidebar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { ThemeToggle } from "./ThemeToggle";

function isPrepPath(pathname: string): boolean {
  return (
    pathname === "/dashboard/avatar" ||
    pathname === "/dashboard/office" ||
    pathname === "/dashboard/sprites" ||
    pathname === "/dashboard" ||
    pathname === "/dashboard/"
  );
}

function isFullBleedPath(pathname: string): boolean {
  return isPrepPath(pathname) || pathname === "/dashboard/map-layout";
}

function headerTitle(pathname: string): string {
  if (pathname.startsWith("/dashboard/avatar")) return "Avatar";
  if (pathname.startsWith("/dashboard/office")) return "Office";
  if (pathname.startsWith("/dashboard/sprites")) return "Sprites";
  if (pathname === "/dashboard/map-layout") return "Map layout";
  if (pathname.startsWith("/dashboard/settings")) return "Settings";
  if (pathname.startsWith("/dashboard/profile")) return "Profile";
  return "Dashboard";
}

function DashboardLayoutInner({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const { isMobile, setMobileOpen } = useSidebar();
  const location = useLocation();
  const prep = isPrepPath(location.pathname);
  const fullBleed = isFullBleedPath(location.pathname);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header
          className={cn(
            "flex items-center justify-between border-b border-border bg-background px-4 md:px-6",
            fullBleed ? "h-12" : "h-16",
          )}
        >
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="shrink-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            {isMobile ? (
              <span className="font-display truncate text-lg font-bold">Nymbl Vyllage</span>
            ) : (
              <div className="text-sm text-muted-foreground">{headerTitle(location.pathname)}</div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {session?.user && (
              <Link
                to="/dashboard/profile"
                title="Profile"
                className="rounded-full ring-offset-background transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <UserAvatar user={session.user} className="h-8 w-8" />
              </Link>
            )}
          </div>
        </header>

        <main
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            fullBleed
              ? prep
                ? "overflow-hidden p-0"
                : "overflow-hidden p-3 md:p-4"
              : "overflow-y-auto p-4 md:p-6",
          )}
        >
          <div className={cn(fullBleed ? "flex min-h-0 flex-1 flex-col" : "mx-auto max-w-5xl")}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <DashboardLayoutInner>{children}</DashboardLayoutInner>
    </SidebarProvider>
  );
}
